/* istanbul ignore file */
/* eslint-disable max-len,react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import {
  Button, Form, Modal, Spinner,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import ChannelType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelType';
import { useSelector } from 'react-redux';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-solid-svg-icons';
import ErrorBanner from '@avtechsoftware/react-component-library/src/components/common/ErrorBanner';
import SettingsMainContent from '../SettingsMainContent';
import { CrudStatus as AvtechApiStatus } from '../../../../../../utility/types/CrudStatus';
import {
  selectSensorUpdateStatus,
  selectSingleDeviceErrors,
} from '../../../../../../redux/slices/singleDeviceSlice/singleDeviceSlice';
import { adjustToDisplay, parseChannelKey } from '../../../../../../utility/channelUtils';
import { ConversionType } from '../../../DeviceSensorsDashboard/sensors/common/ConversionType';
import { selectSessionInformation } from '../../../../../../redux/slices/sessionSlice';

const BasicSensorSettingsModal = ({
  show, handleClose, sensorLabel, channelGroup, onSave, isHidden,
}) => {
  const [error, setError] = useState('');
  const [label, setLabel] = useState('');
  const [channelData, setChannelData] = useState({});
  const [isEdited, setIsEdited] = useState(false);
  const [tempIsHidden, setTempIsHidden] = useState(isHidden);
  const { temperatureScale } = useSelector(selectSessionInformation);
  const preferencesDisplay = { temperatureScale };
  const saveStatus = useSelector(selectSensorUpdateStatus);
  const validationErrors = useSelector(selectSingleDeviceErrors);

  useEffect(() => {
    if (!isEdited) {
      // eslint-disable-next-line no-mixed-operators
      const scale = preferencesDisplay && preferencesDisplay.temperatureScale || 'C';
      setLabel(sensorLabel);
      setChannelData(channelGroup.channels.reduce((acc, channel) => {
        const parsedKey = parseChannelKey(channel.channelKey);
        const channelInstance = ChannelService.getChannelGroupChannel(
          parsedKey.channelGroupType,
          parsedKey.channelIndex,
          parsedKey.channelType,
        );
        acc[channel.channelKey] = {
          adjust: adjustToDisplay(channelInstance, channel.adjust !== undefined ? channel.adjust : 0, scale),
          conversion: parseChannelKey(channel.channelKey).channelType === ChannelType.ANALOG_COMPUTED.id ? channel.conversion : { type: ConversionType.NONE },
        };
        return acc;
      }, {}));
    }
  }, [channelGroup, isEdited, sensorLabel]);

  useEffect(() => {
    if (!show) {
      setIsEdited(false);
      setTempIsHidden(isHidden);
    }
  }, [show]);

  const handleLabelChange = (newLabel) => {
    setIsEdited(true);
    setLabel(newLabel);
  };

  const handleAdjustChange = (key, value) => {
    setIsEdited(true);
    setChannelData({
      ...channelData,
      [key]: {
        ...channelData[key],
        adjust: value,
      },
    });
  };

  const handleSubmit = async () => {
    const result = {
      label,
      channelData,
      isHidden: tempIsHidden !== isHidden ? tempIsHidden : isHidden,
    };
    try {
      const success = await onSave(result);
      if (success) {
        handleClose();
      }
    } catch (err) {
      setError(
        err.message || 'Failed to save settings',
      );
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit().then();
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Settings</Modal.Title>
      </Modal.Header>
      {error && (
      <div className="row">
        <div className="col">
          <ErrorBanner onCloseError={() => setError('')}>
            {error}
          </ErrorBanner>
        </div>
      </div>
      )}
      {/* //hide sensor */}
      <Button
        variant="link"
        className="text-muted text-left ml-3 mt-2 mb-2 p-0"
        onClick={() => setTempIsHidden(!tempIsHidden)}
      >
        <FontAwesomeIcon
          icon={tempIsHidden ? faEye : faEyeSlash}
          className="mr-2"
        />
        {tempIsHidden ? 'Show sensor' : 'Hide sensor'}
      </Button>

      <Modal.Body>
        <Form onKeyDown={handleKeyDown}>
          <SettingsMainContent
            label={label}
            handleLabelChange={handleLabelChange}
            channelGroup={channelGroup}
            channelValues={Object.fromEntries(Object.entries(channelData).map(([key, data]) => [key, data.adjust]))}
            handleAdjustChange={handleAdjustChange}
            validationErrors={validationErrors}
            preferencesDisplay={preferencesDisplay}
          />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={saveStatus === AvtechApiStatus.PENDING}>
          {saveStatus === AvtechApiStatus.PENDING
            ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

BasicSensorSettingsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  sensorLabel: PropTypes.string.isRequired,
  channelGroup: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  isHidden: PropTypes.bool.isRequired,
};

export default BasicSensorSettingsModal;
